import React from 'react';
import { ThumbnailSize } from '../../types/viewComposition';
import API_BASE_URL from '../../config';

// TODO: Extract these to shared utilities in Phase 2
// For now, these are duplicated from InventoryContent.tsx

// Normalize Google Drive URLs and convert GCS URLs to HTTPS
const normalizeDriveUrl = (url?: string | null) => {
  if (!url) return url || null;
  
  // Convert GCS URLs to HTTPS URLs for browser compatibility
  if (url.startsWith('gs://')) {
    return url.replace('gs://', 'https://storage.googleapis.com/');
  }
  
  try {
    const u = new URL(url);
    if (u.hostname === 'drive.google.com' && u.pathname.includes('/file/d/')) {
      const fileId = u.pathname.split('/file/d/')[1]?.split('/')[0];
      if (fileId) {
        return `https://drive.google.com/thumbnail?id=${fileId}&sz=w400`;
      }
    }
  } catch (e) {
    // Invalid URL, return as-is
  }
  return url;
};

// ThumbProxy component - loads authenticated images
interface ThumbProxyProps {
  imageId: number;
  token: string;
  alt: string;
  fallbackUrl?: string;
  isGuestToken?: boolean;
  size: ThumbnailSize;
}

const ThumbProxy: React.FC<ThumbProxyProps> = ({ imageId, token, alt, fallbackUrl, isGuestToken, size }) => {
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    let objectUrl: string | null = null;

    const loadImage = async () => {
      try {
        // Use full-size images only for x-large to prevent fuzziness on high-DPI displays
        const useFullSize = size === 'x-large';
        const url = isGuestToken 
          ? `${API_BASE_URL}/api/collections/guest/${token}/images/${imageId}${useFullSize ? '' : '?thumbnail=true'}`
          : `${API_BASE_URL}/api/images/${imageId}/content${useFullSize ? '' : '?thumb=true'}`;
        
        const headers: HeadersInit = isGuestToken 
          ? {} 
          : { 'Authorization': `Bearer ${token}` };
        
        const response = await fetch(url, { headers });
        
        if (!response.ok) {
          // Silently handle errors - don't spam console
          // Common causes: 401 (auth expired), 500 (GCS download failure), 404 (deleted image)
          if (mounted) {
            setError(true);
          }
          return;
        }
        
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        
        if (mounted) {
          setBlobUrl(objectUrl);
        }
      } catch (err) {
        // Silently handle network errors
        if (mounted) {
          setError(true);
        }
      }
    };

    loadImage();

    return () => {
      mounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [imageId, token, isGuestToken, size]);

  if (error && fallbackUrl) {
    return <img src={fallbackUrl} alt={alt} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
  }

  if (!blobUrl) {
    return <div style={{ width: '100%', height: '100%', background: 'var(--color-bg-tertiary)' }} />;
  }

  return <img src={blobUrl} alt={alt} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
};

interface ItemThumbnailProps {
  item: {
    id: number;
    name: string;
    image_id?: number | null;
    thumbnail_url?: string | null;
    image_url?: string | null;
    categories?: Array<{ id: number; name?: string; key?: string; label?: string }>;
    [key: string]: any;
  };
  size: ThumbnailSize;
  token?: string | null;
  onClick?: (e: React.MouseEvent) => void;
  showCompletedBadge?: boolean;
  renderPlaceholder?: () => React.ReactNode;
  isGuestToken?: boolean;
  fillWidth?: boolean; // Fill parent width instead of using fixed size
}

export const ItemThumbnail: React.FC<ItemThumbnailProps> = ({
  item,
  size,
  token,
  onClick,
  showCompletedBadge,
  renderPlaceholder,
  isGuestToken,
  fillWidth,
}) => {
  const hasImage = item.thumbnail_url || item.image_url || item.image_id;
  
  const sizeClass = `item-thumb-${size}`;
  
  // Use backend proxy (ThumbProxy) if:
  // 1. Guest token (always needs backend auth)
  // 2. No URL but have image_id (fallback to backend)
  // 3. GCS URL (buckets have public access prevention, need backend proxy)
  // 4. gs:// URL (always needs backend proxy)
  const thumbnailOrImageUrl = item.thumbnail_url || item.image_url || '';
  const isGcsUrl = thumbnailOrImageUrl.includes('storage.googleapis.com') || thumbnailOrImageUrl.startsWith('gs://');
  const shouldUseProxy = isGuestToken || 
                        (!item.thumbnail_url && !item.image_url && item.image_id) ||
                        (isGcsUrl && item.image_id);
  
  // When fillWidth is true, use 100% width instead of fixed size class
  const containerStyle = fillWidth ? { width: '100%', aspectRatio: '1' } : {};
  
  return (
    <div className={`item-thumb-container ${fillWidth ? '' : sizeClass}`} style={containerStyle}>
      {/* Completed badge for checklist items */}
      {showCompletedBadge && (
        <div className="item-completed-badge">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>
      )}
      
      <div 
        className="item-thumb"
        onClick={onClick}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
      >
        {hasImage ? (
          <>
            {shouldUseProxy && item.image_id && token ? (
              <ThumbProxy
                key={`thumb-${item.id}-${item.image_id}`}
                imageId={item.image_id}
                token={token}
                alt={item.name}
                fallbackUrl={normalizeDriveUrl(item.thumbnail_url || item.image_url || undefined) || undefined}
                isGuestToken={isGuestToken}
                size={size}
              />
            ) : (
              <img
                src={normalizeDriveUrl(item.thumbnail_url || item.image_url || undefined) || ''}
                alt={item.name}
                loading="lazy"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            )}
          </>
        ) : (
          renderPlaceholder?.()
        )}
      </div>
    </div>
  );
};
